import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import {
  useBillingStatus,
  useOpenPortal,
  useStartCheckout,
  type SubscriptionStatus,
} from '../../../../hooks/useBilling.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/billing',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'admin') throw redirect({ to: '/app' });
  },
  component: BillingPage,
});

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment overdue',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  none: 'No subscription',
};

const PLAN_LABEL: Record<string, string> = {
  monthly: 'Monthly (per seat)',
  annual: 'Annual (per seat)',
  none: '—',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-line last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

function BillingPage() {
  const billing = useBillingStatus();
  const checkout = useStartCheckout();
  const portal = useOpenPortal();
  const busy = checkout.isPending || portal.isPending;

  return (
    <div>
      <AdminPageHeader
        title="Billing"
        description="Manage your Admitly subscription. Seats bill at one per active team member."
      />

      {billing.isLoading && <p className="text-muted">Loading…</p>}
      {billing.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn&rsquo;t load billing: {(billing.error as Error).message}
        </p>
      )}

      {billing.data && (
        <div className="max-w-2xl space-y-6">
          {!billing.data.configured && (
            <div
              className="rounded-lg px-4 py-3 text-[13px]"
              style={{ backgroundColor: '#E3EEF7', color: '#1E5D8F', border: '1px solid #C5DCEC' }}
            >
              Billing isn&rsquo;t enabled on this environment yet — nothing is charged and access
              is never restricted.
            </div>
          )}

          {billing.data.isComped && (
            <div
              className="rounded-lg px-4 py-3 text-[13px]"
              style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
            >
              Your agency has complimentary access — you won&rsquo;t be billed.
            </div>
          )}

          <div className="surface p-5">
            <Row label="Status" value={STATUS_LABEL[billing.data.status]} />
            <Row label="Plan" value={PLAN_LABEL[billing.data.plan] ?? '—'} />
            <Row label="Active seats" value={String(billing.data.seatCount)} />
            <Row
              label={billing.data.status === 'trialing' ? 'Trial ends' : 'Next charge'}
              value={formatDate(billing.data.currentPeriodEnd)}
            />
          </div>

          {/* Actions — hidden for comped agencies (nothing to manage). */}
          {!billing.data.isComped && billing.data.configured && (
            <div className="flex flex-wrap items-center gap-3">
              {billing.data.status === 'none' ? (
                <>
                  <Button loading={busy} onClick={() => checkout.mutate('monthly')}>
                    Subscribe monthly
                  </Button>
                  <Button variant="secondary" loading={busy} onClick={() => checkout.mutate('annual')}>
                    Subscribe annually
                  </Button>
                </>
              ) : (
                <Button loading={busy} onClick={() => portal.mutate()}>
                  Manage billing
                </Button>
              )}
            </div>
          )}

          {(checkout.isError || portal.isError) && (
            <p className="text-sm text-danger">Couldn&rsquo;t reach billing. Please try again.</p>
          )}
        </div>
      )}
    </div>
  );
}
